/* global BigInt */
export const QUB_AMOUNT_DECIMALS = 12;
export const EMPTY_QUB_AMOUNT = "0.000000000000";

const groupThousands = (intPart, separator) =>
  intPart.replace(/\B(?=(\d{3})+(?!\d))/g, separator);

export const qubAmountToAtomicBigInt = (value) => {
  const raw = String(value ?? "").trim();
  if (!raw) return 0n;

  const negative = raw.startsWith("-");
  const sanitized = negative ? raw.slice(1) : raw;
  const [intPartRaw = "0", fracPartRaw = ""] = sanitized.split(".");
  const intPart = intPartRaw.replace(/\D/g, "") || "0";
  const fracPart = fracPartRaw
    .replace(/\D/g, "")
    .slice(0, QUB_AMOUNT_DECIMALS)
    .padEnd(QUB_AMOUNT_DECIMALS, "0");
  const atomic =
    BigInt(intPart) * 10n ** BigInt(QUB_AMOUNT_DECIMALS) + BigInt(fracPart || "0");

  return negative ? -atomic : atomic;
};

export const atomicBigIntToQubAmount = (value) => {
  const atomic = typeof value === "bigint" ? value : BigInt(value || 0);
  const negative = atomic < 0n;
  const normalized = negative ? -atomic : atomic;
  const divisor = 10n ** BigInt(QUB_AMOUNT_DECIMALS);
  const intPart = normalized / divisor;
  const fracPart = String(normalized % divisor).padStart(QUB_AMOUNT_DECIMALS, "0");

  return `${negative ? "-" : ""}${intPart}.${fracPart}`;
};

export const formatQubAmount = (
  value,
  {
    decimals = QUB_AMOUNT_DECIMALS,
    minimum = 0,
    group = false,
    thousandsSeparator = ",",
    decimalSeparator = ".",
  } = {}
) => {
  const raw = String(value ?? "").trim();
  const normalizedMinimum =
    minimum == null ? null : Number(minimum);
  const decimalPattern = /^-?\d*(?:\.\d*)?$/;

  if (raw && decimalPattern.test(raw)) {
    const negative = raw.startsWith("-");
    const unsignedRaw = negative ? raw.slice(1) : raw;
    let [intPart = "0", fracPart = ""] = unsignedRaw.split(".");

    intPart = intPart.replace(/\D/g, "") || "0";
    fracPart = fracPart.replace(/\D/g, "");

    if (normalizedMinimum != null && Number.isFinite(normalizedMinimum)) {
      const numericValue = Number(`${negative ? "-" : ""}${intPart}.${fracPart}`);
      if (Number.isFinite(numericValue) && numericValue < normalizedMinimum) {
        return formatQubAmount(normalizedMinimum, {
          decimals,
          minimum: null,
          group,
          thousandsSeparator,
          decimalSeparator,
        });
      }
    }

    intPart = intPart.replace(/^0+(?=\d)/, "") || "0";

    if (decimals <= 0) {
      const displayInt = group
        ? groupThousands(intPart, thousandsSeparator)
        : intPart;
      return `${negative && intPart !== "0" ? "-" : ""}${displayInt}`;
    }

    const displayFrac = fracPart
      .slice(0, decimals)
      .padEnd(decimals, "0");
    const displayInt = group
      ? groupThousands(intPart, thousandsSeparator)
      : intPart;

    return `${negative && (intPart !== "0" || /[1-9]/.test(displayFrac)) ? "-" : ""}${displayInt}${decimalSeparator}${displayFrac}`;
  }

  const amount = Number(value);

  if (!Number.isFinite(amount)) {
    return decimals > 0
      ? `0${decimalSeparator}${"0".repeat(decimals)}`
      : "0";
  }

  const normalized = minimum == null ? amount : Math.max(minimum, amount);
  const fixed = normalized.toFixed(decimals);

  if (decimals <= 0) {
    return group ? groupThousands(fixed, thousandsSeparator) : fixed;
  }

  let [intPart, fracPart] = fixed.split(".");
  if (group) {
    intPart = groupThousands(intPart, thousandsSeparator);
  }

  return `${intPart}${decimalSeparator}${fracPart}`;
};
